/**
 * 작성자 : 홍선영
 * 날짜 : 2023.05.02
 * 경로 : 일반관리-현장관리-현장정보탭
 */

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { IComCdList, IModal } from 'customTypes';
import { msiteUseYnState, userState } from '../../../atoms';
import { COMCD_INFRA, COMCD_S_STATUS, COMCD_S_VERSION, COMCD_USE_YN, INIT_USE_YN_A, SELECT_SNAME_OR_HNAME } from '../../../_constants';

import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnGhost } from '../../../components/Button';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import SelectBox from '../../../components/SelectBox';
import ViewTable from '../../../components/Table/ViewTable';
import BackButton from '../../../components/BackButton';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import ConfirmModal from '../../../components/Modal/ConfirmModal';
import SiteInfoInputForm from '../../../components/Form/SiteInfoInputForm';
import { ynFilter } from '../../../utils/ynFilter';
import { trimObject } from '../../../utils/trimObject';
import { setComCdListState } from '../../../utils/setComCdListState';
import { logPost } from '../../../services/log';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import MSiteList from './MSiteList';
import { getHcdList } from '../../../hooks/useMSite';
import InputRowList from '../../../components/Form/InputRowList';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../../utils/useDebounce';
import { AddressSearchModal } from '../../../components/AddressSearch';
import useBackHistory from '../../../hooks/useBackHistory';

const Root = styled.div`
  &.hideRoot {
    visibility: hidden;
    position: absolute !important;
  }
  .inputForm-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  > .inputFormsWrapper {
    height: 3.5rem !important;
    flex-shrink: 0;
    display: flex;
    flex-direction: row !important;
    align-items: center;
    padding: 0 0.5rem;
    border-bottom: 1px solid var(--outline);
    &.smallTab {
      flex-direction: row;
      align-items: center;
      padding: 0 0.5rem;
      font-weight: 500;
      &::-webkit-scrollbar {
        -webkit-appearance: none;
        height: 0rem !important;
      }
      .tab {
        color: var(--text-secondary);
        display: flex;
        align-items: center;
        padding: 0 0.5rem;
        cursor: pointer;
        height: 3.5rem;
        border-bottom: 2px solid transparent;
      }
      .activeTab {
        font-weight: 700;
        border-bottom: 2px solid var(--selected-primary);
        color: var(--selected-primary);
      }
    }
    @media (min-width: 1025px) {
      padding: 0 1rem;
    }
  }
`;

const SiteInfo = ({ type }: { type: 'm' | 'c' }) => {
  const { t } = useTranslation();
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const node = useRef<any>(null);
  const userInfoInputFormRef = useRef<any>(null); // 인풋 폼 ref (테이블 로우를 클릭했을 때 바로 inputForm 으로 스크롤 이동시키기 위한 ref)
  const [searchOptionUseYn, setSearchOptionUseYn] = useState(INIT_USE_YN_A);
  const [searchOption, setSearchOption] = useState({ type: 'nameType', nameType: 'sName', cdName: t('현장명') });
  const [searchName, setSearchName] = useState({ sName: '', hName: '' });
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [rowState, setRowState] = useState({
    address1: '',
    address2: '',
    bigo: '',
    eDate: '',
    editor: '',
    fDate: '',
    hCd: '',
    hName: '',
    hTranscd: '',
    hTransyn: 'N', // 기본값
    postNum: '',
    sCd: '',
    sDate: '',
    infraCd: '',
    sName: '',
    sStatus: 'Y', // 기본값
    sTel: '',
    sCeoname: '',
    sVersion: '',
    smsYn: 'N', // 기본값
    useYn: 'Y', // 기본값
    delYn: 'N', // 기본값
    wDate: '',
    writer: '',
    latitude: '',
    longitude: '',
    eExcelYn: 'Y',
    eListYn: 'Y',
    ePrintYn: 'Y',
    msiteYn: 'N',
    eduexamYn: 'N',
    solapiLimitMonth: 3000,
  }); // 서버에서 받아 온 raw data 어레이 중 1줄의 데이터 state
  const [viewData, setViewData] = useState([{}]); // 서버에서 받아 온 raw data 중 viewTable에 뿌려줄 데이터만 담는 state
  const [columnCount, setColumnCount] = useState(0);
  const [masterSiteList, setMasterSiteList] = useState<any>([]);
  const [orgMasterSiteList, setOrgMasterSiteList] = useState<any>([]); // 필터링되지않은 본사리스트
  const [hCd, setHCd] = useState<any>({ type: 'hCd', hCd: '', cdName: '' });
  const [sStatus, setSStatus] = useState<any>({ type: COMCD_S_STATUS, [COMCD_S_STATUS]: 'Y', cdName: '운영중', cdSort: 0 }); // 현장상태 운영여부 공통코드
  const [sVersion, setSVersion] = useState<any>({ type: COMCD_S_VERSION, [COMCD_S_VERSION]: '', cdName: '', cdSort: 0 }); // 프로그램버전 공통코드
  const [infraCd, setSInfra] = useState<any>({ type: COMCD_INFRA, [COMCD_INFRA]: '', cdName: '', cdSort: 0 }); // 인프라 공통코드
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [sTelNumState, setSTelNumState] = useState({ num1: '', num2: '', num3: '' });
  const [headList, setHeadList] = useState<{ type: String; hCd: string; cdName: string }[]>([]);
  const [sStatusComCdList, setSStatusComCdList] = useState<IComCdList[]>([]); // 현장상태 운영여부 공통코드
  const [sVersionComCdList, setSVersionComCdList] = useState<IComCdList[]>([]); // 프로그램버전 공통코드
  const [sInfraComCdList, setSInfraComCdList] = useState<IComCdList[]>([]); // 인프라 공통코드
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [viewMainPage, setViewMainPage] = useState(true);
  const originalSnapshotRef = useRef<string | null>(null);
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true); // 사용여부 공통코드 목록 (검색조건에서 사용, 전체포함)
  const { data: useYnComCdList } = useFetchCommonCodeList(COMCD_USE_YN, false); // 사용유무 공통코드 목록 (사용자 정보에서 사용)
  const [selectedTab, setSelectedTab] = useState<'siteDetail' | 'siteList' | 'siteManager'>('siteDetail');
  const setMsiteUseYn = useSetRecoilState(msiteUseYnState);
  const [isClick, setIsClick] = useState(false); // 클릭 시에만 userQuery 동작하기 위한 상태

  const debouncedhName = useDebounce(searchName.hName);
  const debouncedsName = useDebounce(searchName.sName);
  /*
  const {
    data: companyCcharge,
    isLoading,
    isError,
    refetch,
  } = GetFetchStatus({
    path: '/company/ccharge',
    req: { hCd: rowState.hCd, sCd: rowState.sCd, cCd: userInfo.hCd },
    queryKey: `${rowState.hCd}_${rowState.sCd}`,
    useLoginUserInfo: false,
  });
  */

  // 사내 현장 담당자 조회
  const { isLoading, isError } = useQuery(['/company/ccharge'], () => apiGet({ path: '/company/ccharge', req: { hCd: rowState.hCd, sCd: rowState.sCd, cCd: userInfo.hCd } }), {
    enabled: isClick && !!userInfo.hCd && !!userInfo.sCd,
    onSuccess: (data) => {
      setIsClick(false);
      setCompanyManagerList(data.data.data);
      setInitCompanyManagerList(data.data.data);
    },
  });
  const [companyManagerList, setCompanyManagerList] = useState<any[] | any>(null);
  const [initCompanyManagerList, setInitCompanyManagerList] = useState<any[] | any>(null);

  /*
  useEffect(() => {
    if (companyCcharge) {
      setCompanyManagerList(companyCcharge);
      setInitCompanyManagerList(companyCcharge);
    }
  }, [companyCcharge]);
  */
  useEffect(() => {
    getSiteListAPI().then((res: any) => {
      if (res.status === 200) {
        setSearchOptionUseYn(INIT_USE_YN_A);
        setComCdListState(COMCD_S_STATUS, setSStatusComCdList, false);
        setComCdListState(COMCD_S_VERSION, setSVersionComCdList, false);
        setComCdListState(COMCD_INFRA, setSInfraComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '현장 관리 > 현장 정보',
          action: '조회',
          etc: ``,
        });
      }
    });

    // 본사 리스트 조회 API
    getHcdList(type, userInfo.hCd).then((res) => setHeadList(res));
  }, []);

  useEffect(() => {
    // 도메인에 따라서 뷰테이블에 전달해야하는 키값만 viewTableProperties 배열안에 작성하면 됨.
    if (masterSiteList && masterSiteList.length > 0) {
      const viewTableProperties = ['hCd', 'hName', 'sCd', 'sName', 'useYn'];
      setDataIntoViewTable(viewTableProperties);
    }
  }, [masterSiteList]);

  // 로우데이터에서 뷰테이블에 보여줄 데이터만 뽑아서 배열에 담아 리턴
  const setDataIntoViewTable = (properties: string[]) => {
    setColumnCount(properties.length);
    const viewDataArray: any[] = masterSiteList.map((el: any) => {
      // properties 순서대로 값을 추출
      const result = properties.reduce((next, key) => {
        if (Object.prototype.hasOwnProperty.call(el, key)) {
          return { ...next, [key]: el[key] };
        }
        return next;
      }, {});
      return result;
    });
    setViewData(viewDataArray);
  };

  // 삭제버튼 클릭으로 삭제 모달창 띄우기
  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteHeadAPI }));
  };

  // 현장정보 조회 API
  const getSiteListAPI = async () => {
    const res = await apiGet(type === 'm' ? { path: '/site' } : { path: '/company/csite', req: { cCd: userInfo.hCd } });
    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      // 삭제되지 않은 현장만 필터링해서 setState
      if (type === 'm') {
        const activeSiteInfos = data.siteInfoList.filter((el: any) => el.delYn === 'N');
        setMasterSiteList(activeSiteInfos);
        setOrgMasterSiteList(activeSiteInfos);
      } else {
        const newMap = data.map((el: any) => ({
          ...el,
          hTranscd: '',
          hTransyn: 'N', // 기본값
          infraCd: '',
          sVersion: '',
          smsYn: 'N', // 기본값
          eExcelYn: 'Y',
          eListYn: 'Y',
          ePrintYn: 'Y',
          msiteYn: 'N',
          eduexamYn: 'N',
        }));
        setMasterSiteList(newMap);
        setOrgMasterSiteList(newMap);
      }
    } else {
      // toast.error(t(ERROR));
    }
    return res;
  };

  // 현장정보 저장&수정 API
  const buildSaveRequestRaw = () => {
    const sTelMergedNumber = `${sTelNumState.num1}-${sTelNumState.num2}-${sTelNumState.num3}`;
    const resolvedSTel = sTelMergedNumber !== '--' ? sTelMergedNumber : rowState.sTel || '';
    const commonData = {
      hCd: hCd.hCd,
      sName: rowState.sName,
      sCd: rowState.sCd,
      sStatus: rowState.sStatus,
      sDate: rowState.sDate?.replaceAll('-', ''),
      fDate: rowState.fDate?.replaceAll('-', ''),
      sTel: resolvedSTel,
      postNum: rowState.postNum,
      address1: rowState.address1,
      address2: rowState.address2,
      bigo: rowState.bigo,
      useYn: rowState.useYn,
      delYn: rowState.delYn,
      latitude: rowState.latitude,
      longitude: rowState.longitude,
    };

    const reqData =
      type === 'm'
        ? {
            ...commonData,
            infraCd: rowState.infraCd,
            sVersion: rowState.sVersion,
            postNum: rowState.postNum,
            address1: rowState.address1,
            address2: rowState.address2,
            smsYn: rowState.smsYn,
            hTransyn: rowState.hTransyn,
            hTranscd: rowState.hTranscd,
            writer: userInfo.userId,
            editor: userInfo.userId,
            eExcelYn: rowState.eExcelYn,
            eListYn: rowState.eListYn,
            ePrintYn: rowState.ePrintYn,
            msiteYn: rowState.msiteYn,
            eduexamYn: rowState.eduexamYn,
            solapiLimitMonth: Number(rowState.solapiLimitMonth) || 3000,
          }
        : {
            ...commonData,
            cCd: userInfo.hCd,
            sCeoname: rowState.sCeoname,
          };

    return reqData;
  };

  const buildSaveRequest = () => trimObject(buildSaveRequestRaw());

  const getSaveSnapshot = () => JSON.stringify(buildSaveRequestRaw());

  // 기존 데이터 기준 스냅샷 저장 (레코드 변경 시)
  useEffect(() => {
    if (isNewAdd) {
      originalSnapshotRef.current = null;
      return;
    }
    if (rowState.sCd || rowState.hCd) {
      originalSnapshotRef.current = getSaveSnapshot();
    }
  }, [rowState.sCd, rowState.hCd, isNewAdd]);

  const saveSiteAPI = async () => {
    const req = buildSaveRequest();
    const res = await apiPost(type === 'm' ? { path: '/site', contentType: 'application/json', req } : { path: '/company/csite', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      if (type === 'm') {
        applyFilter(data[1].siteInfoList);
        setOrgMasterSiteList(data[1].siteInfoList);
        setRowState(data[0].siteInfo);
        setMsiteUseYn(rowState.msiteYn as 'N' | 'Y');

        toast.success(t('현장정보 저장&수정 성공'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '마스터일반관리-현장관리',
          action: '현장 정보 저장&수정',
          etc: `본사명 : ${rowState.hName} / 현장명 : ${rowState.sName}(${rowState.sCd || '신규'})`,
        });
        originalSnapshotRef.current = getSaveSnapshot();
      } else {
        const newMap = data.map((el: any) => ({
          ...el,
          sCeoname: el.sCeoname,
          hTranscd: '',
          hTransyn: 'N', // 기본값
          infraCd: '',
          sVersion: '',
          smsYn: 'N', // 기본값
          eExcelYn: 'Y',
          eListYn: 'Y',
          ePrintYn: 'Y',
          msiteYn: 'N',
          eduexamYn: 'N',
        }));
        applyFilter(newMap);
        setOrgMasterSiteList(newMap);
        setIsNewAdd(false);
        if (!req.sCd) {
          const newItems = data.filter((item2: any) => !orgMasterSiteList.some((item1: any) => item1.hCd === item2.hCd && item1.sCd === item2.sCd));
          if (newItems?.length > 0) setRowState(newItems[0]);
        }
        toast.success(t('현장정보 저장&수정 성공'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '설정관리-고객사관리',
          action: '현장 정보 저장&수정',
          etc: `본사명 : ${rowState.hName} / 현장명 : ${rowState.sName}(${rowState.sCd || '신규'})`,
        });
        originalSnapshotRef.current = getSaveSnapshot();
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장정보 삭제 API
  const deleteHeadAPI = async () => {
    if (type === 'm') {
      const req = { hCd: rowState.hCd, sCd: rowState.sCd, editor: userInfo.userId };
      const res = await apiDelete({ path: '/site', req });
      const { statusCode, data } = res.data;
      if (statusCode === 200) {
        applyFilter(data[1].siteInfoList);
        setOrgMasterSiteList(data[1].siteInfoList);
        setIsNewAdd(true);
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '마스터일반관리-현장관리',
          action: '현장 정보 삭제',
          etc: `본사명 : ${rowState.hName} / 현장명 : ${rowState.sName}(${rowState.sCd})`,
        });
      } else {
        // toast.error(t(ERROR));
      }
    } else {
      const req = { cCd: userInfo.hCd, hCd: rowState.hCd, sCd: rowState.sCd };
      const res = await apiDelete({ path: '/company/csite', req });
      const { statusCode, data } = res.data;
      if (statusCode === 200) {
        applyFilter(data);
        setOrgMasterSiteList(data);
        setIsNewAdd(true);
        toast.success(t('삭제되었습니다'));
        initiateState();
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '설정관리-고객사관리-현장정보',
          action: '현장 정보 삭제',
          etc: `본사명 : ${rowState.hName} / 현장명 : ${rowState.sName}(${rowState.sCd})`,
        });
      } else {
        // toast.error(t(ERROR));
      }
    }
  };

  // 저장버튼 클릭해서 save API 호출하여 데이터전송
  const onClickSave = () => {
    setIsSaveClicked(true);
    if (hCd.hCd?.trim() === '') return toast.warning(t('본사명을 선택하세요'));
    if (rowState.sName?.trim() === '') return toast.warning(t('현장명을 입력하세요'));
    // 동일 본사 내 중복 현장명 체크 (자기 자신 제외)
    const targetHCd = hCd.hCd;
    const normalizedNewName = (rowState.sName || '').trim().toLowerCase();
    const isDuplicateName = orgMasterSiteList?.some((el: any) => {
      const sameHead = el.hCd === targetHCd;
      const sameName = (el.sName || '').trim().toLowerCase() === normalizedNewName;
      const differentPk = el.sCd !== rowState.sCd; // 자기 자신은 제외
      return sameHead && sameName && differentPk;
    });

    if (normalizedNewName && isDuplicateName) {
      return toast.warning(t('이미 동일한 현장명이 존재합니다'));
    }
    const hasChanges = !isNewAdd && originalSnapshotRef.current && originalSnapshotRef.current !== getSaveSnapshot();
    if (hasChanges) {
      setOpenModal((prev: IModal) => ({
        ...prev,
        status: true,
        type: 'confirm',
        title: '정말 수정하시겠습니까?',
        api: saveSiteAPI,
      }));
      return undefined;
    }
    if (!isNewAdd && !hasChanges) {
      return toast.warning(t('변경된 내용이 없습니다'));
    }
    return saveSiteAPI();
  };

  const resetSelectAllOptions = () => {
    setHCd({ type: 'hCd', hCd: '', cdName: '' });
    setSStatus({ type: COMCD_S_STATUS, [COMCD_S_STATUS]: 'Y', cdName: '운영중', cdSort: 0 });
    setSInfra({ type: COMCD_INFRA, [COMCD_INFRA]: '', cdName: '미선택' });
    setSVersion({ type: COMCD_S_VERSION, [COMCD_S_VERSION]: '', cdName: '', cdSort: 0 });
  };

  // 신규등록 버튼 클릭해서 새 입력창(iputForm) 노출
  const onClickNewRegistration = () => {
    initiateState();
    setIsNewAdd(true);
    resetSelectAllOptions();

    if (size.innerSize.W <= 1024) {
      setViewMainPage(false);
    }
  };

  const backToMain = () => {
    setViewMainPage(true);
  };

  const { onBackPrev } = useBackHistory({ isOpen: !viewMainPage, onPrev: backToMain, pageName: 'siteInfo' });

  // 스테이트 객체의 밸류를 ''로 초기화
  const initiateState = () => {
    Object.keys(rowState).map((el: any) => {
      return setRowState((prev: any) => ({
        ...prev,
        [el]: '',
        useYn: 'Y',
        delYn: 'N',
        hTransyn: 'N',
        smsYn: 'N',
        sStatus: 'Y',
        sVersion: '',
        infraCd: '',
        eExcelYn: 'Y',
        eListYn: 'Y',
        ePrintYn: 'Y',
        msiteYn: 'N',
        eduexamYn: 'N',
        solapiLimitMonth: 3000,
      }));
    });
    setIsNewAdd(true);
    setIsSaveClicked(false);
  };

  const applyFilter = (array: any[]) => {
    // 필터링 기준
    const filterOptions = {
      nameType: searchOption.nameType,
      hName: searchName.hName,
      sName: searchName.sName,
      useYn: searchOptionUseYn[COMCD_USE_YN],
    };
    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = array.filter((item: any) => {
      if (filterOptions.nameType === 'hName') {
        return item[searchOption.nameType]?.toLowerCase()?.includes(filterOptions.hName?.toLowerCase());
      }
      return item[searchOption.nameType]?.toLowerCase()?.includes(filterOptions.sName?.toLowerCase());
    });

    if ((searchOption.nameType === 'hName' && searchName.hName === '') || (searchOption.nameType === 'sName' && searchName.sName === '')) {
      const result = ynFilter(array, 'useYn', searchOptionUseYn[COMCD_USE_YN]);
      if (result.length > 0) {
        setMasterSiteList(result);
      } else {
        setMasterSiteList([]);
      }
    } else {
      const result = ynFilter(filteredArray, 'useYn', searchOptionUseYn[COMCD_USE_YN]);
      if (result.length > 0) {
        setMasterSiteList(result);
      } else {
        setMasterSiteList([]);
      }
    }
  };

  useEffect(() => {
    applyFilter(orgMasterSiteList);
  }, [searchOption.nameType, debouncedhName, debouncedsName, searchOptionUseYn[COMCD_USE_YN]]);

  const onClickInitiateSearchOption = () => {
    setSearchOptionUseYn(INIT_USE_YN_A);
    setSearchOption({ type: 'nameType', nameType: 'sName', cdName: t('현장명') });
    setSearchName({ sName: '', hName: '' });

    if (size.innerSize.W > 1024) {
      onClickNewRegistration(); // 우측 인풋창도 초기화
    }
  };

  useEffect(() => {
    setSelectedTab('siteDetail');
  }, [rowState, setSelectedTab]);

  const postAPI = async (array: any[]) => {
    try {
      const newArray = array.map((el) => ({ ...el, hCd: rowState.hCd, sCd: rowState.sCd, cCd: userInfo.hCd, chTel: el.chTel === '010--' ? '' : el.chTel }));
      const res = await apiPost({ path: '/company/ccharge', req: { cchargeInfoReqDto: newArray } });
      const { statusCode, message, data } = res.data;
      if (statusCode === 200) {
        toast.success(t(message));
        setCompanyManagerList(data);
      }
    } catch (error) {
      throw new Error('error');
    }
  };

  const deleteAPI = async (chCd: string) => {
    try {
      const res = await apiDelete({ path: '/company/ccharge', req: { hCd: rowState.hCd, sCd: rowState.sCd, cCd: userInfo.hCd, chCd } });
      const { statusCode, message } = res.data;
      if (statusCode === 200) {
        toast.success(t(message));
        setCompanyManagerList(res.data.data);
      }
    } catch (error) {
      throw new Error('error');
    }
  };

  // 주소버튼 클릭으로 주소검색 모달창 띄우기
  const onClickAddressSearch = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'addressSearch' }));
  };

  const onClickTab = (currentTab: string) => {
    if (currentTab === 'siteManager') {
      setSelectedTab('siteManager');
      setIsClick(!false);
    }
    if (currentTab === 'siteList') {
      setSelectedTab('siteList');
    }
    if (currentTab === 'siteDetail') {
      setSelectedTab('siteDetail');
    }
  };

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root className={size.innerSize.W > 1024 || viewMainPage ? 'showRoot' : 'hideRoot'}>
        <SearchOptions>
          {size.innerSize.W > 1024 ? (
            <>
              <div className='inputsWrapper'>
                <div className='searchResult'>
                  {t('총')}
                  <span>{masterSiteList?.length}</span>
                  {t('개')}
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t('사용유무')}</label>
                    <SelectBox
                      options={useYnComCdListWithAll}
                      defaultOption={t('전체')}
                      state={searchOptionUseYn}
                      setState={setSearchOptionUseYn}
                      stateKey={COMCD_USE_YN}
                      filterbar='filter-1-left'
                      initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                    />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <SelectBox
                      options={SELECT_SNAME_OR_HNAME}
                      defaultOption={searchOption.nameType === 'sName' ? t('현장명') : t('본사명')}
                      state={searchOption}
                      setState={setSearchOption}
                      stateKey='nameType'
                      initiateKey={searchOption.nameType}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input
                      label=''
                      placeholder={searchOption.nameType === 'sName' ? t('현장명') : t('본사명')}
                      type='text'
                      id={searchOption.nameType === 'sName' ? 'sName' : 'hName'}
                      name={searchOption.nameType === 'sName' ? 'sName' : 'hName'}
                      state={searchName}
                      setState={setSearchName}
                    />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickNewRegistration}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: '100%' }}>
                  <div className='inputForm-col'>
                    <Input
                      label=''
                      placeholder={searchOption.nameType === 'sName' ? t('현장명') : t('본사명')}
                      type='search'
                      id={searchOption.nameType === 'sName' ? 'sName' : 'hName'}
                      name={searchOption.nameType === 'sName' ? 'sName' : 'hName'}
                      state={searchName}
                      setState={setSearchName}
                      ico='search'
                    />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickNewRegistration}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        <div className='searchContent inputForm-body' ref={node}>
          <ViewTable
            rawData={masterSiteList.sort((a: any, b: any) => a.hName.localeCompare(b.hName) || a.sName.localeCompare(b.sName))}
            state={viewData}
            setRowState={setRowState}
            setIsNewAdd={setIsNewAdd}
            userInfoInputFormRef={userInfoInputFormRef}
            setHCd={setHCd}
            setSStatus={setSStatus}
            setSVersion={setSVersion}
            setSInfra={setSInfra}
            setViewMainPage={() => setViewMainPage(false)}
          />
        </div>
      </Root>
      <Root className={size.innerSize.W > 1024 || !viewMainPage ? 'showRoot' : 'hideRoot'}>
        <div className={`inputFormsWrapper ${isNewAdd ? 'flexRowEm' : 'smallTab'}`}>
          {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
          {isNewAdd ? (
            <div className='formTitle'>{t('새로운 현장정보')}</div>
          ) : (
            <>
              <div
                className={`tab ${selectedTab === 'siteDetail' ? 'activeTab' : ''}`} //
                role='button'
                tabIndex={0}
                onClick={() => onClickTab('siteDetail')}
              >
                {t('현장정보 상세')}
              </div>
              {type === 'm' && masterSiteList.find((el: any) => el.sCd === rowState.sCd && el.hCd === rowState.hCd)?.msiteYn === 'Y' && (
                <div
                  className={`tab ${selectedTab === 'siteList' ? 'activeTab' : ''}`} //
                  role='button'
                  tabIndex={0}
                  onClick={() => onClickTab('siteList')}
                >
                  {t('다중 현장 관리 목록')}
                </div>
              )}
              {/** 사내설정관리>고객사관리메뉴 */}
              {type === 'c' && (
                <div
                  className={`tab ${selectedTab === 'siteManager' ? 'activeTab' : ''}`} //
                  role='button'
                  tabIndex={0}
                  onClick={() => onClickTab('siteManager')}
                >
                  {t('현장 담당자')}
                </div>
              )}
            </>
          )}
        </div>
        {selectedTab === 'siteDetail' && (
          <SiteInfoInputForm
            state={rowState}
            setState={setRowState}
            isNewAdd={isNewAdd}
            userInfoInputFormRef={userInfoInputFormRef}
            onClickNewAdd={onClickNewRegistration}
            onClickSave={onClickSave}
            onClickDelete={onClickDelete}
            sStatus={sStatus}
            setSStatus={setSStatus}
            sVersion={sVersion}
            setSVersion={setSVersion}
            sTelNumState={sTelNumState}
            setSTelNumState={setSTelNumState}
            infraCd={infraCd}
            setSInfra={setSInfra}
            hList={headList}
            useYnComCdList={useYnComCdList}
            sStatusComCdList={sStatusComCdList}
            sVersionComCdList={sVersionComCdList}
            sInfraComCdList={sInfraComCdList}
            isSaveClicked={isSaveClicked}
            hCd={hCd}
            setHCd={setHCd}
            type={type}
            onClickAddressSearch={onClickAddressSearch}
          />
        )}
        {selectedTab === 'siteList' && (
          <MSiteList
            headList={headList} //
            rowState={rowState}
          />
        )}
        {selectedTab === 'siteManager' && companyManagerList && (
          <InputRowList
            rowPk='chCd'
            initRow={{ chCd: '', chName: '', partName: '', chTel: '010--', bigo: '' }}
            data={companyManagerList}
            isLoading={!companyManagerList}
            isError={isError}
            rowHeaderList={[
              { idx: 0, dataKey: 'chCd', className: 'trCol2p5 flex-center tableStickyNo', headerName: '코드', inputType: 'disabledInput', required: false },
              { idx: 1, dataKey: 'chName', className: 'trCol8 flex-center', headerName: '담당자명', inputType: 'text', required: true, maxLength: 25 },
              { idx: 2, dataKey: 'partName', className: 'trCol8 flex-center', headerName: '직함', inputType: 'text', required: false, maxLength: 20 },
              { idx: 3, dataKey: 'chTel', className: 'trCol15 flex-center', headerName: '연락처', inputType: 'tel', required: false },
              { idx: 4, dataKey: 'bigo', className: 'trCol8 flex-center content-overflow', headerName: '비고', inputType: 'text', required: false, maxLength: 50 },
            ]}
            postAPI={postAPI}
            deleteAPI={deleteAPI}
          />
        )}
      </Root>
      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
        {openModal && openModal.type === 'confirm' && <ConfirmModal openModal={openModal} setOpenModal={setOpenModal} text={t('정말 수정하시겠습니까?')} />}
        {openModal && openModal.type === 'addressSearch' && <AddressSearchModal openModal={openModal} setOpenModal={setOpenModal} setState={setRowState} />}
      </Portal>
    </div>
  );
};

export default SiteInfo;
